import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  type Firestore,
  type FirestoreDataConverter,
  type Unsubscribe,
} from 'firebase/firestore';
import type { BlockLibraryExampleDoc } from '@modules/shared-types';
import { blockLibraryConverter } from '../converters/blockLibraryConverter';
import { CoreFirebaseService } from './CoreFirebaseService';

const SYSTEM_BLOCK_LIBRARY_COLLECTION = 'systemBlockLibrary';

/**
 * READ-ONLY client for the root `systemBlockLibrary` collection — the system
 * tier of the Block Examples Library. Docs share the project-tier shape
 * (`BlockLibraryExampleDoc`, blueprint/preview stored as JSON strings) and reuse
 * `blockLibraryConverter`. All writes go through
 * `scripts/seed-system-block-library.ts` (admin SDK); this client deliberately
 * exposes no mutation surface, matching the auth-read / admin-write rules.
 */
export class SystemBlockLibraryService {
  private readonly firestore: Firestore;

  constructor(
    private readonly converter: FirestoreDataConverter<BlockLibraryExampleDoc> = blockLibraryConverter,
    firestore?: Firestore,
  ) {
    this.firestore = firestore ?? CoreFirebaseService.getInstance().firestore;
  }

  async list(options: { enabledOnly?: boolean } = {}): Promise<BlockLibraryExampleDoc[]> {
    const snapshot = await getDocs(
      collection(this.firestore, SYSTEM_BLOCK_LIBRARY_COLLECTION).withConverter(this.converter),
    );
    return readStrictExamples(snapshot.docs)
      .filter((item) => !options.enabledOnly || item.enabled)
      .sort(sortExamples);
  }

  async get(id: string): Promise<BlockLibraryExampleDoc | null> {
    const snapshot = await getDoc(
      doc(this.firestore, SYSTEM_BLOCK_LIBRARY_COLLECTION, id).withConverter(this.converter),
    );
    return snapshot.exists() ? snapshot.data() : null;
  }

  subscribe(
    onData: (examples: BlockLibraryExampleDoc[]) => void,
    onError?: (error: Error) => void,
  ): Unsubscribe {
    const ref = collection(this.firestore, SYSTEM_BLOCK_LIBRARY_COLLECTION).withConverter(this.converter);
    return onSnapshot(ref, (snapshot) => {
      onData(readStrictExamples(snapshot.docs).sort(sortExamples));
    }, (error) => onError?.(error));
  }
}

/**
 * A legacy or corrupt root record must not hide unrelated strict-v2 examples.
 * This is deliberately a skip, never a v1 fallback: each returned item still
 * passes through the strict converter independently.
 */
function readStrictExamples(
  docs: Array<{ id: string; data: () => BlockLibraryExampleDoc }>,
): BlockLibraryExampleDoc[] {
  const examples: BlockLibraryExampleDoc[] = [];
  for (const item of docs) {
    try {
      examples.push(item.data());
    } catch (error) {
      console.warn(`[SystemBlockLibraryService] Ignoring invalid strict-v2 record ${item.id}:`, error);
    }
  }
  return examples;
}

function sortExamples(left: BlockLibraryExampleDoc, right: BlockLibraryExampleDoc): number {
  return left.name.localeCompare(right.name) || left.id.localeCompare(right.id);
}
